import { Address } from "@msuite/ui/address";
import { useTranslation } from "@msuite/ui/localization";
import type { Nullable, Tables } from "@msuite/utils/types";

/** Props Interface */
interface ConnectPartnerConfirmationProps {
	organization: Pick<
		Tables<"organizations">,
		"id" | "name" | "color" | "streetName" | "houseNumber" | "zipCode" | "city"
	>;
	partnership: Nullable<Tables<"organizationPartnerships">>;
}

/**
 * The confirmation before connecting, shown by the connect modal and the `/connect/:code` page:
 * the organisation that owns the code becomes the host and may book the viewer's crews, so it is
 * named before anything is written.
 */
export function ConnectPartnerConfirmation({
	organization,
	partnership,
}: ConnectPartnerConfirmationProps) {
	/** Context */
	const { t } = useTranslation();

	/** Computed */
	// An organisation that never filled in its address shows its name alone, not a placeholder.
	const hasAddress = Boolean(organization.streetName || organization.zipCode || organization.city);

	/** Render */
	return (
		<div className="gap-4 v-stack">
			<div className="gap-1 v-stack">
				<p className="font-semibold">{organization.name}</p>
				{hasAddress && <Address item={organization} />}
			</div>
			<p className="label">
				{partnership
					? t("organization.already_connected_with", { name: organization.name })
					: t("organization.connect_confirmation_message", { name: organization.name })}
			</p>
		</div>
	);
}
